import { useEffect, useState } from "react";
import { X, Plus, Loader2, CheckCircle2, Building2 } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

export default function BankerNewTaskModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (taskId: number) => void;
}) {
  const [companies, setCompanies] = useState<any[]>([]);
  const [form, setForm] = useState({
    companyId: "",
    title: "",
    investorIntent: "",
    category: "banker_initiated",
    priority: "medium",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    fetch(`${BASE}api/banker/companies`, { headers: { "x-banker-token": token } })
      .then(r => r.ok ? r.json() : [])
      .then((cos) => setCompanies(cos.filter((c: any) => c.covered)))
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.companyId) { setError("Pick a company from your coverage"); return; }
    if (!form.title) { setError("Give the task a title"); return; }
    setError(null); setSaving(true);
    const token = localStorage.getItem("banker_token");
    try {
      const r = await fetch(`${BASE}api/banker/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
        body: JSON.stringify({
          companyId: parseInt(form.companyId, 10),
          title: form.title,
          investorIntent: form.investorIntent || null,
          category: form.category,
          priority: form.priority,
        }),
      });
      if (r.ok) { const t = await r.json(); onCreated(t.id); onClose(); }
      else { const e = await r.json(); setError(e.error || "Create failed"); }
    } catch { setError("Network error"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2"><Plus className="w-5 h-5 text-[#FF5C00]" /> Log a task</h2>
            <p className="text-white/50 text-xs mt-1">Capture outbound work you're about to do or a founder ask that came in off-platform. Meera will see this in your context.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-400 text-xs mb-3">{error}</div>}

        <div className="space-y-3">
          <div>
            <label className="text-white/60 text-xs mb-1.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> Company</label>
            <select value={form.companyId} onChange={(e) => setForm(f => ({ ...f, companyId: e.target.value }))}
              className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white">
              <option value="">Select a covered company...</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-white/60 text-xs mb-1.5">Title</label>
            <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Prep diligence pack for series A round"
              className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30" />
            <p className="text-[10px] text-white/40 mt-1">Investor-linked tasks are created automatically by Meera from Kavya conversations. This is for your own outbound work with the founder.</p>
          </div>

          <div>
            <label className="block text-white/60 text-xs mb-1.5">Context / what needs to happen (optional)</label>
            <textarea value={form.investorIntent} onChange={(e) => setForm(f => ({ ...f, investorIntent: e.target.value }))}
              rows={4} placeholder="Background, the ask, what done looks like. Meera uses this when you ask her about it later."
              className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 leading-relaxed" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Category</label>
              <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white">
                <option value="banker_initiated">Banker-initiated</option>
                <option value="intro_request">Intro request</option>
                <option value="diligence">Diligence</option>
                <option value="info_request">Info request</option>
                <option value="meeting_followup">Meeting follow-up</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Priority</label>
              <select value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white">
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={submit} disabled={saving}
              className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><CheckCircle2 className="w-4 h-4" /> Create task</>}
            </button>
            <button onClick={onClose} className="text-white/60 hover:text-white px-4 py-2 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
